import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons';
import useIsMobile, { useMaxWidth } from '../../hooks/useIsMobile';
import DashboardSectionTitle from '../shared/DashboardSectionTitle';

const DAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
/** Monthly summary never shows more than one rolling year at a time. */
const MONTHLY_SUMMARY_PAGE_SIZE = 12;

const formatCurrency = (v) =>
  v >= 0 ? `+$${v.toFixed(2)}` : `-$${Math.abs(v).toFixed(2)}`;

const formatPercent = (v) => `${(v * 100).toFixed(1)}%`;

export function formatDateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function parseDateKey(str) {
  if (!str) return null;
  const d = new Date(`${str}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function addDays(d, n) {
  const next = new Date(d);
  next.setDate(next.getDate() + n);
  return next;
}

function startOfWeekSunday(d) {
  const s = new Date(d);
  s.setHours(12, 0, 0, 0);
  s.setDate(s.getDate() - s.getDay());
  return s;
}

function endOfWeekSaturday(d) {
  const start = startOfWeekSunday(d);
  return addDays(start, 6);
}

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1, 12, 0, 0, 0);
}

function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 12, 0, 0, 0);
}

function eachDay(from, to) {
  const days = [];
  let cur = new Date(from);
  cur.setHours(12, 0, 0, 0);
  const end = new Date(to);
  end.setHours(12, 0, 0, 0);
  while (cur <= end) {
    days.push(new Date(cur));
    cur = addDays(cur, 1);
  }
  return days;
}

const isWeekday = (day) => {
  const dow = day.getDay();
  return dow >= 1 && dow <= 5;
};

export function getPresetVisibleRange(timeframe) {
  const today = new Date();
  today.setHours(12, 0, 0, 0);

  if (timeframe === 'today' || timeframe === 'week') {
    return { start: startOfWeekSunday(today), end: endOfWeekSaturday(today) };
  }
  if (timeframe === 'month') {
    // Rolling ~30 days ending today (matches backend TIMEFRAME_DAYS.month), week-aligned for the grid.
    const rangeStart = addDays(today, -29);
    return {
      start: startOfWeekSunday(rangeStart),
      end: endOfWeekSaturday(today),
      rangeStart,
      rangeEnd: today,
    };
  }
  return null;
}

export function getCalendarMode(timeframe, dateRange) {
  if (dateRange?.since && dateRange?.until) {
    const since = parseDateKey(dateRange.since);
    const until = parseDateKey(dateRange.until);
    if (!since || !until) return 'weekGrid';
    const diffDays = Math.round((until - since) / 86400000) + 1;
    const sameMonth =
      since.getMonth() === until.getMonth() && since.getFullYear() === until.getFullYear();
    if (diffDays <= 7) return 'weekGrid';
    // Custom ranges up to ~45 days use a multi-week grid so mid-month windows aren't clipped.
    if (diffDays <= 45) return 'weekGrid';
    if (sameMonth) return 'monthGrid';
    return 'monthlySummary';
  }
  if (timeframe === 'today' || timeframe === 'week' || timeframe === 'month') return 'weekGrid';
  return 'monthlySummary';
}

/**
 * Visible calendar fetch window. For month, uses a rolling ~30-day week-aligned grid
 * (not the calendar month — that hid prior-month trades still in the analytics window).
 */
export function getMonthGridFetchRange(timeframe, dateRange) {
  const mode = getCalendarMode(timeframe, dateRange);

  if (dateRange?.since && dateRange?.until && mode === 'weekGrid') {
    const since = parseDateKey(dateRange.since);
    const until = parseDateKey(dateRange.until);
    if (!since || !until) return null;
    const diffDays = Math.round((until - since) / 86400000) + 1;
    if (diffDays <= 7) {
      return {
        since: formatDateKey(startOfWeekSunday(since)),
        until: formatDateKey(endOfWeekSaturday(since)),
      };
    }
    return {
      since: formatDateKey(startOfWeekSunday(since)),
      until: formatDateKey(endOfWeekSaturday(until)),
    };
  }

  if (timeframe === 'month' && !dateRange) {
    const visible = getPresetVisibleRange('month');
    return {
      since: formatDateKey(visible.start),
      until: formatDateKey(visible.end),
    };
  }

  if (mode !== 'monthGrid') return null;

  const today = new Date();
  today.setHours(12, 0, 0, 0);

  let ref = today;
  if (dateRange?.since) {
    ref = parseDateKey(dateRange.since) || today;
  }

  const monthStart = startOfMonth(ref);
  const monthEnd = endOfMonth(ref);
  const gridStart = startOfWeekSunday(monthStart);
  const gridEnd = endOfWeekSaturday(monthEnd);

  return { since: formatDateKey(gridStart), until: formatDateKey(gridEnd) };
}

function buildDayMap(days) {
  const map = new Map();
  for (const d of days || []) {
    if (d?.date) map.set(d.date, d);
  }
  return map;
}

function weekTotal(daysInWeek, dayMap) {
  let pnl = 0;
  let trades = 0;
  for (const d of daysInWeek) {
    const key = formatDateKey(d);
    const row = dayMap.get(key);
    if (row) {
      pnl += row.pnl || 0;
      trades += row.trades || 0;
    }
  }
  return { pnl, trades };
}

function aggregateByMonth(days) {
  const byMonth = new Map();
  for (const d of days || []) {
    const date = parseDateKey(d.date);
    if (!date) continue;
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const prev = byMonth.get(key) || { monthKey: key, pnl: 0, trades: 0, days: 0 };
    prev.pnl += d.pnl || 0;
    prev.trades += d.trades || 0;
    prev.days += 1;
    byMonth.set(key, prev);
  }
  return [...byMonth.values()].sort((a, b) => a.monthKey.localeCompare(b.monthKey));
}

function monthKeysBetween(start, end) {
  const keys = [];
  let cur = new Date(start.getFullYear(), start.getMonth(), 1, 12, 0, 0, 0);
  const last = new Date(end.getFullYear(), end.getMonth(), 1, 12, 0, 0, 0);
  while (cur <= last) {
    keys.push(`${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}`);
    cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1, 12, 0, 0, 0);
  }
  return keys;
}

function getSummaryMonthSpan(timeframe, dateRange) {
  const today = new Date();
  today.setHours(12, 0, 0, 0);

  if (dateRange?.since && dateRange?.until) {
    const since = parseDateKey(dateRange.since);
    const until = parseDateKey(dateRange.until);
    if (since && until) return { start: since, end: until };
  }

  const spans = {
    quarter: 90,
    ytd: null,
    year: 365,
    '2y': 730,
    '5y': 1825,
  };
  if (timeframe === 'ytd') {
    const start = new Date(today.getFullYear(), 0, 1, 12, 0, 0, 0);
    return { start, end: today };
  }
  const days = spans[timeframe];
  if (days) {
    const start = addDays(today, -(days - 1));
    return { start, end: today };
  }

  return null;
}

function buildMonthlySummaryRows(days, timeframe, dateRange) {
  const aggregated = aggregateByMonth(days);
  const byKey = new Map(aggregated.map((m) => [m.monthKey, m]));
  const span = getSummaryMonthSpan(timeframe, dateRange);

  if (!span) {
    return aggregated;
  }

  return monthKeysBetween(span.start, span.end).map(
    (monthKey) =>
      byKey.get(monthKey) || { monthKey, pnl: 0, trades: 0, days: 0 }
  );
}

function monthLabel(monthKey) {
  const [y, m] = monthKey.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

const gridClassName = (weekdaysOnly) =>
  weekdaysOnly
    ? 'performance-calendar-grid performance-calendar-grid--weekdays'
    : 'performance-calendar-grid';

const EmptyDayCell = ({ colors, compact }) => (
  <div
    className={`performance-calendar-day performance-calendar-day--empty${compact ? ' performance-calendar-day--compact' : ''}`}
    style={{
      background: colors.bgPrimary,
      borderColor: colors.border,
    }}
    aria-hidden
  />
);

const DayCell = ({
  day,
  dayData,
  colors,
  compact,
  compactFace,
  truncatePnl,
  inRange = true,
  onDayClick,
}) => {
  const dateKey = formatDateKey(day);

  if (!inRange) {
    return <EmptyDayCell colors={colors} compact={compact || compactFace} />;
  }

  const hasTrades = dayData && dayData.trades > 0;
  const isProfit = hasTrades && dayData.pnl >= 0;
  const accentColor = hasTrades ? (isProfit ? colors.chartUp : colors.chartDown) : colors.border;
  const showInlinePnl = !compactFace;
  const isClickable = !!onDayClick;

  const shortDate = compactFace
    ? day.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' })
    : day.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  const cellClass = [
    'performance-calendar-day',
    compact || compactFace ? 'performance-calendar-day--compact' : '',
    compactFace ? 'performance-calendar-day--link' : '',
    isClickable ? 'performance-calendar-day--clickable' : '',
    hasTrades ? 'performance-calendar-day--has-trades' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const inner = (
    <>
      <div className="performance-calendar-day-date" style={{ color: colors.textMuted }}>
        {shortDate}
      </div>
      {showInlinePnl && (
        <>
          {dayData ? (
            <>
              <div
                className={`performance-calendar-day-pnl${truncatePnl ? ' performance-calendar-day-pnl--truncate' : ''}`}
                style={{ color: accentColor }}
              >
                {formatCurrency(dayData.pnl)}
              </div>
              {!compact && (
                <div className="performance-calendar-day-meta" style={{ color: colors.textLight }}>
                  {dayData.trades} trades · {formatPercent(dayData.winRate)} win
                </div>
              )}
            </>
          ) : (
            <div className="performance-calendar-day-pnl" style={{ color: colors.textMuted }}>
              —
            </div>
          )}
        </>
      )}
      {compactFace && hasTrades && (
        <span
          className="performance-calendar-day-dot"
          style={{ backgroundColor: accentColor }}
          aria-hidden
        />
      )}
      {compactFace && !hasTrades && (
        <div className="performance-calendar-day-pnl" style={{ color: colors.textMuted }}>
          —
        </div>
      )}
    </>
  );

  const style = {
    background: colors.bgSecondary,
    borderColor: colors.border,
    borderLeftColor: hasTrades ? accentColor : colors.border,
  };

  if (isClickable) {
    return (
      <button
        type="button"
        className={cellClass}
        style={style}
        onClick={() => onDayClick(dateKey, dayData || null)}
        aria-label={`View trades for ${shortDate}`}
      >
        {inner}
      </button>
    );
  }

  return (
    <div className={cellClass} style={style}>
      {inner}
    </div>
  );
};

const WeekTotalCell = ({ total, colors, compact }) => {
  const hasData = total.trades > 0;
  const color = !hasData ? colors.textMuted : total.pnl >= 0 ? colors.chartUp : colors.chartDown;
  return (
    <div
      className={`performance-calendar-week-total${compact ? ' performance-calendar-week-total--compact' : ''}`}
      style={{
        background: colors.bgCard,
        borderColor: colors.border,
      }}
    >
      <div className="performance-calendar-week-total-label" style={{ color: colors.textMuted }}>
        Week
      </div>
      <div
        className={`performance-calendar-week-total-pnl${compact ? ' performance-calendar-week-total-pnl--truncate' : ''}`}
        style={{ color }}
      >
        {hasData ? formatCurrency(total.pnl) : '—'}
      </div>
      {hasData && !compact && (
        <div style={{ fontSize: '0.68rem', color: colors.textMuted, marginTop: 2 }}>
          {total.trades} trades
        </div>
      )}
    </div>
  );
};

const WeekGridView = ({
  weekRows,
  dayMap,
  colors,
  rangeStart,
  rangeEnd,
  weekdaysOnly,
  compactFace,
  truncatePnl,
  onDayClick,
}) => {
  const headers = weekdaysOnly ? WEEKDAY_HEADERS : DAY_HEADERS;

  return (
    <>
      <div className={gridClassName(weekdaysOnly)}>
        {headers.map((label) => (
          <div key={label} className="performance-calendar-header-cell" style={{ color: colors.textMuted }}>
            {label}
          </div>
        ))}
        <div
          className="performance-calendar-header-cell performance-calendar-header-cell--total"
          style={{ color: colors.textMuted }}
        >
          Total
        </div>
      </div>
      {weekRows.map((weekDays, rowIdx) => {
        const visibleDays = weekdaysOnly ? weekDays.filter(isWeekday) : weekDays;
        const daysForTotal = weekDays.filter(
          (day) => (!rangeStart || day >= rangeStart) && (!rangeEnd || day <= rangeEnd)
        );
        const total = weekTotal(daysForTotal, dayMap);
        return (
          <div key={rowIdx} className={gridClassName(weekdaysOnly)}>
            {visibleDays.map((day) => {
              const key = formatDateKey(day);
              const inRange = (!rangeStart || day >= rangeStart) && (!rangeEnd || day <= rangeEnd);
              return (
                <DayCell
                  key={key}
                  day={day}
                  dayData={inRange ? dayMap.get(key) : null}
                  colors={colors}
                  compact={compactFace}
                  compactFace={compactFace}
                  truncatePnl={truncatePnl}
                  inRange={inRange}
                  onDayClick={onDayClick}
                />
              );
            })}
            <WeekTotalCell total={total} colors={colors} compact={compactFace || truncatePnl} />
          </div>
        );
      })}
    </>
  );
};

const MonthGridView = ({
  monthStart,
  dayMap,
  colors,
  weekdaysOnly,
  compactFace,
  truncatePnl,
  onDayClick,
}) => {
  const gridStart = startOfWeekSunday(monthStart);
  const monthEnd = endOfMonth(monthStart);
  const gridEnd = endOfWeekSaturday(monthEnd);
  const allDays = eachDay(gridStart, gridEnd);

  const weekRows = [];
  for (let i = 0; i < allDays.length; i += 7) {
    weekRows.push(allDays.slice(i, i + 7));
  }

  const month = monthStart.getMonth();
  const headers = weekdaysOnly ? WEEKDAY_HEADERS : DAY_HEADERS;

  return (
    <>
      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: colors.text, marginBottom: '12px' }}>
        {monthStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
      </div>
      <div className={gridClassName(weekdaysOnly)}>
        {headers.map((label) => (
          <div key={label} className="performance-calendar-header-cell" style={{ color: colors.textMuted }}>
            {label}
          </div>
        ))}
        <div
          className="performance-calendar-header-cell performance-calendar-header-cell--total"
          style={{ color: colors.textMuted }}
        >
          Total
        </div>
      </div>
      {weekRows.map((weekDays, rowIdx) => {
        const total = weekTotal(
          weekDays.filter((d) => d.getMonth() === month),
          dayMap
        );
        const visibleDays = weekdaysOnly ? weekDays.filter(isWeekday) : weekDays;
        return (
          <div key={rowIdx} className={gridClassName(weekdaysOnly)}>
            {visibleDays.map((day) => {
              const inMonth = day.getMonth() === month;
              const key = formatDateKey(day);
              if (!inMonth) {
                return <div key={key} className="performance-calendar-day-spacer" aria-hidden />;
              }
              return (
                <DayCell
                  key={key}
                  day={day}
                  dayData={dayMap.get(key)}
                  colors={colors}
                  compact={compactFace}
                  compactFace={compactFace}
                  truncatePnl={truncatePnl}
                  onDayClick={onDayClick}
                />
              );
            })}
            <WeekTotalCell total={total} colors={colors} compact={compactFace || truncatePnl} />
          </div>
        );
      })}
    </>
  );
};

const MonthlySummaryView = ({ months, colors, isMobile, maxAbsPnl }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: isMobile ? '8px' : '12px',
    }}
  >
    {months.map((m) => {
      const isProfit = m.pnl >= 0;
      const accent = isProfit ? colors.chartUp : colors.chartDown;
      const intensity = maxAbsPnl > 0 ? Math.min(1, Math.abs(m.pnl) / maxAbsPnl) : 0.3;
      // Kept light: the accent border and the coloured P&L figure already encode direction,
      // so a heavy fill on every tile just competes with the numbers.
      const bg = isProfit
        ? `rgba(7, 203, 163, ${0.06 + intensity * 0.14})`
        : `rgba(247, 50, 99, ${0.06 + intensity * 0.14})`;

      // No tooltip here: it duplicated the tile's own contents and, being hover-only,
      // never opened on touch anyway.
      return (
        <div
          key={m.monthKey}
          style={{
            padding: isMobile ? '10px' : '14px 16px',
            borderRadius: '10px',
            border: `1px solid ${colors.border}`,
            borderLeft: `4px solid ${m.trades > 0 ? accent : colors.border}`,
            background: m.trades > 0 ? bg : colors.bgSecondary,
            opacity: m.trades > 0 ? 1 : 0.75,
            minWidth: 0,
          }}
        >
          <div style={{ fontSize: isMobile ? '0.6875rem' : '0.95rem', fontWeight: 700, color: colors.text, marginBottom: isMobile ? 3 : 4 }}>
            {monthLabel(m.monthKey)}
          </div>
          <div
            style={{
              fontSize: isMobile ? '0.8125rem' : '1.1rem',
              fontWeight: 700,
              color: m.trades > 0 ? accent : colors.textMuted,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {m.trades > 0 ? formatCurrency(m.pnl) : '—'}
          </div>
          <div style={{ fontSize: isMobile ? '0.6875rem' : '0.78rem', color: colors.textMuted, marginTop: isMobile ? 3 : 3 }}>
            {m.trades} trade{m.trades === 1 ? '' : 's'} · {m.days} day{m.days === 1 ? '' : 's'}
          </div>
        </div>
      );
    })}
  </div>
);

const PerformanceDashboardCalendar = ({ calendarData, timeframe, dateRange, colors, onDayClick, titleOutside = false }) => {
  const isMobile = useIsMobile();
  const isNarrowCalendar = useMaxWidth(640);
  const isCompactCalendar = useMaxWidth(900);

  const weekdaysOnly = isNarrowCalendar;
  const compactFace = isNarrowCalendar;
  const truncatePnl = isCompactCalendar && !compactFace;

  const days = calendarData?.days ?? [];
  const dayMap = useMemo(() => buildDayMap(days), [days]);
  const mode = getCalendarMode(timeframe, dateRange);

  // 0 = newest 12-month window; higher = older
  const [monthPageOffset, setMonthPageOffset] = useState(0);

  useEffect(() => {
    setMonthPageOffset(0);
  }, [timeframe, dateRange?.since, dateRange?.until]);

  const { weekRows, rangeStart, rangeEnd, monthStart, months } = useMemo(() => {
    const today = new Date();
    today.setHours(12, 0, 0, 0);

    if (mode === 'weekGrid') {
      let start;
      let end;
      let rangeStart = null;
      let rangeEnd = null;
      if (dateRange?.since && dateRange?.until) {
        const since = parseDateKey(dateRange.since);
        const until = parseDateKey(dateRange.until);
        const diffDays = Math.round((until - since) / 86400000) + 1;
        start = startOfWeekSunday(since);
        end = endOfWeekSaturday(diffDays <= 7 ? since : until);
        rangeStart = since;
        rangeEnd = until;
      } else if (timeframe === 'month') {
        const visible = getPresetVisibleRange('month');
        start = visible.start;
        end = visible.end;
        rangeStart = visible.rangeStart;
        rangeEnd = visible.rangeEnd;
      } else {
        start = startOfWeekSunday(today);
        end = endOfWeekSaturday(today);
      }
      const allDays = eachDay(start, end);
      const weekRows = [];
      for (let i = 0; i < allDays.length; i += 7) {
        weekRows.push(allDays.slice(i, i + 7));
      }
      return {
        weekRows,
        rangeStart,
        rangeEnd,
        monthStart: null,
        months: [],
      };
    }

    if (mode === 'monthGrid') {
      let ref = today;
      if (dateRange?.since) {
        ref = parseDateKey(dateRange.since) || today;
      }
      return {
        weekRows: [],
        rangeStart: null,
        rangeEnd: null,
        monthStart: startOfMonth(ref),
        months: [],
      };
    }

    return {
      weekRows: [],
      rangeStart: null,
      rangeEnd: null,
      monthStart: null,
      months: buildMonthlySummaryRows(days, timeframe, dateRange),
    };
  }, [mode, dateRange, days, timeframe]);

  const { visibleMonths, canPageOlder, canPageNewer, monthWindowLabel } = useMemo(() => {
    const total = months.length;
    if (total <= MONTHLY_SUMMARY_PAGE_SIZE) {
      return {
        visibleMonths: months,
        canPageOlder: false,
        canPageNewer: false,
        monthWindowLabel: null,
      };
    }
    const maxOffset = total - MONTHLY_SUMMARY_PAGE_SIZE;
    const offset = Math.min(Math.max(0, monthPageOffset), maxOffset);
    const endExclusive = total - offset;
    const start = Math.max(0, endExclusive - MONTHLY_SUMMARY_PAGE_SIZE);
    const slice = months.slice(start, endExclusive);
    const first = slice[0];
    const last = slice[slice.length - 1];
    const label =
      first && last
        ? `${monthLabel(first.monthKey)} – ${monthLabel(last.monthKey)}`
        : null;
    return {
      visibleMonths: slice,
      canPageOlder: start > 0,
      canPageNewer: offset > 0,
      monthWindowLabel: label,
    };
  }, [months, monthPageOffset]);

  const maxAbsPnl = Math.max(...visibleMonths.map((m) => Math.abs(m.pnl)), 1);
  const tradeDayCount = days.length;
  const showMonthPager = mode === 'monthlySummary' && (canPageOlder || canPageNewer);
  // The monthly summary shows month totals, not day cells — promising day details there is a lie.
  const showTapHint = compactFace && mode !== 'monthlySummary';

  const gridViewProps = {
    dayMap,
    colors,
    weekdaysOnly,
    compactFace,
    truncatePnl,
    onDayClick,
  };

  const monthPager = showMonthPager ? (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexShrink: 0,
      }}
    >
      {!isMobile && monthWindowLabel && (
        <span
          style={{
            fontSize: '0.75rem',
            color: colors.textMuted,
            textTransform: 'none',
            letterSpacing: 0,
            marginRight: 4,
            whiteSpace: 'nowrap',
          }}
        >
          {monthWindowLabel}
        </span>
      )}
      <button
        type="button"
        aria-label="Older months"
        disabled={!canPageOlder}
        onClick={() =>
          setMonthPageOffset((o) => {
            const maxOffset = Math.max(0, months.length - MONTHLY_SUMMARY_PAGE_SIZE);
            return Math.min(o + MONTHLY_SUMMARY_PAGE_SIZE, maxOffset);
          })
        }
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          padding: 0,
          borderRadius: 8,
          border: `1px solid ${colors.border}`,
          background: colors.bgSecondary || colors.bgPrimary,
          color: canPageOlder ? colors.text : colors.textMuted,
          opacity: canPageOlder ? 1 : 0.4,
          cursor: canPageOlder ? 'pointer' : 'default',
        }}
      >
        <ChevronLeft size={16} />
      </button>
      <button
        type="button"
        aria-label="Newer months"
        disabled={!canPageNewer}
        onClick={() => setMonthPageOffset((o) => Math.max(0, o - MONTHLY_SUMMARY_PAGE_SIZE))}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          padding: 0,
          borderRadius: 8,
          border: `1px solid ${colors.border}`,
          background: colors.bgSecondary || colors.bgPrimary,
          color: canPageNewer ? colors.text : colors.textMuted,
          opacity: canPageNewer ? 1 : 0.4,
          cursor: canPageNewer ? 'pointer' : 'default',
        }}
      >
        <ChevronRight size={16} />
      </button>
    </div>
  ) : null;

  return (
    <>
      {titleOutside && (
        <DashboardSectionTitle
          title="Performance Calendar"
          count={tradeDayCount > 0 ? `${tradeDayCount} trading day${tradeDayCount === 1 ? '' : 's'}` : null}
          marginBottom={10}
        >
          {monthPager}
        </DashboardSectionTitle>
      )}
    <div
      className={`performance-calendar-shell${compactFace ? ' performance-calendar-shell--compact' : ''}`}
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: '12px',
        padding: isMobile || compactFace ? '16px' : '20px',
        marginBottom: '24px',
      }}
    >
      {!titleOutside && (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: '16px',
          minWidth: 0,
        }}
      >
        <div
          style={{
            fontSize: '0.875rem',
            color: colors.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            minWidth: 0,
          }}
        >
          Performance Calendar
          {tradeDayCount > 0 && (
            <span style={{ marginLeft: '8px', fontWeight: 400, textTransform: 'none' }}>
              ({tradeDayCount} trading day{tradeDayCount === 1 ? '' : 's'})
            </span>
          )}
          {showTapHint && (
            <span style={{ marginLeft: '8px', fontWeight: 400, textTransform: 'none', fontSize: '0.75rem' }}>
              Tap a day for details
            </span>
          )}
        </div>

        {monthPager}
      </div>
      )}
      {titleOutside && showTapHint && (
        <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginBottom: 12 }}>
          Tap a day for details
        </div>
      )}

      {mode === 'weekGrid' && (
        <WeekGridView
          weekRows={weekRows}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          {...gridViewProps}
        />
      )}

      {mode === 'monthGrid' && monthStart && (
        <MonthGridView monthStart={monthStart} {...gridViewProps} />
      )}

      {mode === 'monthlySummary' && (
        visibleMonths.length === 0 ? (
          <div style={{ color: colors.textLight, fontSize: '0.875rem' }}>No months in this range.</div>
        ) : (
          <MonthlySummaryView months={visibleMonths} colors={colors} isMobile={isMobile} maxAbsPnl={maxAbsPnl} />
        )
      )}

      {mode !== 'monthlySummary' && tradeDayCount === 0 && (
        <div style={{ color: colors.textLight, fontSize: '0.875rem', marginTop: '8px' }}>
          No trades in this range — days above show empty slots.
        </div>
      )}
    </div>
    </>
  );
};

export default PerformanceDashboardCalendar;
